import {
  getTailwindColor,
  isPreview,
  canonCamelCase,
  applyVariables,
} from "../../utils";
import resolveImage from "./resolveImage";
import resolveEntry from "./resolveEntry";
import resolveVariables from "./resolveVariables";

const colorMap = Object.freeze({
  19: getTailwindColor("f5"),
  8: getTailwindColor("f5"),
  17: getTailwindColor("f2"),
  4218: getTailwindColor("f2"),
  16: getTailwindColor("f1"),
});

const getColor = id => {
  return colorMap[id] || getTailwindColor("blue.default");
};

/**
 * @param {object} entry            – product_Entry or chapter_Entry
 * @param {array}  globalVariables  – array of variable_Entry fetched at root
 */
const resolveManual = (
  entry,
  globalVariables = [],
  entryVariableValues = undefined
) => {
  if (entry && entry._resolved) {
    return entry;
  }

  const {
    id = Number(entry.id),
    uid,
    typeHandle,
    __typename,
    siteId,
    language,
    siteHandle,
    userTitle,
    productIcon,
    productCategory,
    productFeatures,
    productVariant,
    productFamily,
    pageLayoutType,
    children = [],
    scopedVariables = [],
  } = entry;

  const type = canonCamelCase(typeHandle || __typename);

  /* -------------------- variable maps ------------------- */
  const { resolvedByGlobalId: variableValues } = resolveVariables({
    globalVariables,
    scopedVariables,
    manualContext: {
      productId: id,
      productCategoryIds: (productCategory || []).map(c => Number(c.id)),
      productFeatureIds: (productFeatures || []).map(f => Number(f.id)),
    },
  });
  /* ------------------------------------------------------ */

  /* -------------------- string helpers ------------------ */
  const applyVars = (str, opts = {}) =>
    applyVariables(str, entryVariableValues || variableValues, opts);

  const category =
    productCategory && productCategory[0]
      ? {
          id: productCategory[0].id,
          title: applyVars(productCategory[0].title),
          role: productCategory[0].role,
        }
      : null;

  const logo = productIcon ? resolveImage(productIcon[0]) : null;
  const safeSiteId = siteId || 1;
  if (!siteId) {
    console.error("manual missing site id, setting to 1");
  }
  const appliedUserTitle = applyVariables(
    userTitle,
    entryVariableValues || variableValues,
    {
      wrap: false,
    }
  );
  const appliedHtmlUserTitle = isPreview()
    ? applyVariables(userTitle, entryVariableValues || variableValues, {
        wrap: isPreview(),
      })
    : undefined;

  return {
    id,
    uid,
    siteId: safeSiteId.toString(10),
    language: language || null,
    siteHandle: siteHandle || null,
    title: appliedUserTitle,
    htmlTitle: appliedHtmlUserTitle,
    userTitle: appliedUserTitle,
    logo,
    type,
    image: logo,
    color: getColor(id),
    category,
    variant: applyVars(productVariant),
    productFamily: applyVars(productFamily),
    productCategory: productCategory || [], // vars not parsed, only use for conditions
    productFeatures: productFeatures || [], // vars not parsed, only use for conditions
    variableValues,
    chapters: children.map(chapter => ({
      // if you change this make sure to update chapter handling in resolveEntry as well
      id: Number(chapter.id),
      title: applyVariables(
        chapter.userTitle,
        entryVariableValues || variableValues,
        { wrap: false }
      ),
      htmlTitle: isPreview()
        ? applyVariables(
            chapter.userTitle,
            entryVariableValues || variableValues,
            {
              wrap: isPreview(),
            }
          )
        : undefined,
      topics: Array.isArray(chapter.topicArticles)
        ? chapter.topicArticles.map((t, i) =>
            resolveEntry(t, i, entryVariableValues || variableValues)
          )
        : [],
    })),
    pageLayoutType,
    _resolved: true,
  };
};

export default resolveManual;
